import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type AccountPasswordResult } from "../api/client";
import { queryKeys } from "../api/queryKeys";
import { useTheme } from "../contexts/ThemeContext";
import { AccountFormFields, type AccountFormValues } from "./AccountFormFields";

type Props = {
  onCreated: (result: AccountPasswordResult) => void;
  onCancel: () => void;
};



/**
 * Création d'un compte V2, en panneau de droite (pas de popup). Le mot de passe est
 * généré côté serveur et affiché une seule fois dans le bandeau du détail du compte.
 */
export function CreateAccountPanel({ onCreated, onCancel }: Props) {
  const { t } = useTheme();
  const queryClient = useQueryClient();

  const { control, handleSubmit, getValues, setValue } = useForm<AccountFormValues>({
    defaultValues: { login: "", nom: "", idEntreprise: "", nomEntreprise: "" },
  });

  const createMut = useMutation({
    mutationFn: (values: AccountFormValues) =>
      api.accounts.create({
        login: values.login.trim(),
        nom: values.nom.trim(),
        idEntreprise: Number(values.idEntreprise),
        nomEntreprise: values.nomEntreprise.trim(),
      }),
    onSuccess: (result) => onCreated(result),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.accounts }),
  });

  const error = createMut.error
    ? createMut.error instanceof Error ? createMut.error.message : "Erreur inconnue"
    : null;

  const onSubmit = handleSubmit((values) => createMut.mutate(values));

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "18px 22px" }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: t.text1 }}>Créer un compte V2</div>
      <div style={{ fontSize: 12.5, color: t.text2, marginTop: 3, marginBottom: 18 }}>
        Le mot de passe est généré automatiquement et affiché une seule fois après création.
      </div>

      <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 460 }}>
        <AccountFormFields control={control} getValues={getValues} setValue={setValue} autoFocusLogin />
        {error && (
          <div style={{ padding: "10px 12px", background: t.lateBg, color: t.lateColor, borderRadius: 8, fontSize: 13 }}>
            {error}
          </div>
        )}
        <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
          <button type="submit" disabled={createMut.isPending} style={{
            border: "none", background: t.btnBg, color: "#fff", borderRadius: 8,
            padding: "10px 16px", fontSize: 13.5, fontWeight: 600, fontFamily: "inherit",
            cursor: createMut.isPending ? "not-allowed" : "pointer",
            opacity: createMut.isPending ? 0.7 : 1,
          }}>
            {createMut.isPending ? "Création..." : "Créer le compte"}
          </button>
          <button type="button" onClick={onCancel} style={{
            border: `1px solid ${t.rowBorder}`, background: t.cardBg, color: t.text2,
            borderRadius: 8, padding: "10px 14px", fontSize: 13.5, cursor: "pointer", fontFamily: "inherit",
          }}>Annuler</button>
        </div>
      </form>
    </div>
  );
}
